"use client"

/**
 * Generic multi-select filter popover. Trigger renders
 * `{labelPrefix}: All {pluralNoun}` / `{labelPrefix}: <one>` /
 * `{labelPrefix}: N {pluralNoun}`; popover renders a scrollable checkbox list
 * with an optional Clear button.
 *
 * Callers pass `{value, label}` pairs (optionally a `subtitle`). Thin wrappers
 * convert their own enum/GID shapes into these pairs — see the catalog status
 * filter for an example.
 */

import { Check, Filter } from "lucide-react"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Checkbox } from "@/components/ui/checkbox"
import { Label } from "@/components/ui/label"
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover"
import { cn } from "@/lib/utils"
import { useSharedUiTranslations } from "@/components/i18n/ui-control-labels-context"


export interface MultiSelectOption {
  value: string
  label: string
  /** Optional secondary line (e.g. handle / SKU) shown below the label. */
  subtitle?: string
}

interface MultiSelectFilterProps {
  /** Trigger button prefix, e.g. "Status" / "Category" / "Collection". */
  labelPrefix: string
  /** Single-item-selected display formatter; defaults to the option's label. */
  formatOneSelected?: (option: MultiSelectOption) => string
  /** Plural-form noun for `N items` (default "items"). */
  pluralNoun?: string
  availableOptions: MultiSelectOption[]
  selectedValues: string[]
  onChange: (values: string[]) => void
  /** Override the trigger button width — defaults to auto. */
  triggerClassName?: string
  /** Override popover content width — defaults to 220px. */
  popoverWidthClassName?: string
}

export function MultiSelectFilter({
  labelPrefix,
  formatOneSelected,
  pluralNoun = "items",
  availableOptions,
  selectedValues,
  onChange,
  triggerClassName,
  popoverWidthClassName,
}: MultiSelectFilterProps) {
  const tUi = useSharedUiTranslations("MultiSelectFilter")

  const triggerLabel = (() => {
    if (selectedValues.length === 0) return `All ${pluralNoun}`
    if (selectedValues.length === 1) {
      const found = availableOptions.find((o) => o.value === selectedValues[0])
      if (found) return formatOneSelected ? formatOneSelected(found) : found.label
      return `1 ${pluralNoun}`.replace(/s$/, "") + " selected"
    }
    return `${selectedValues.length} ${pluralNoun}`
  })()

  const toggle = (value: string) => {
    if (selectedValues.includes(value)) {
      onChange(selectedValues.filter((v) => v !== value))
    } else {
      onChange([...selectedValues, value])
    }
  }

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          size="sm"
          className={cn("h-9 gap-2", triggerClassName)}
        >
          <Filter className="h-3.5 w-3.5 text-muted-foreground" />
          <span className="text-sm">{labelPrefix}:</span>
          <span className="text-sm truncate max-w-[140px]">{triggerLabel}</span>
          {selectedValues.length > 0 && (
            <Badge variant="secondary" className="ml-1 tabular-nums text-[10px] px-1.5">
              {selectedValues.length}
            </Badge>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        className={cn("p-2 max-h-[60vh] overflow-auto", popoverWidthClassName ?? "w-[260px]")}
      >
        <div className="flex flex-col gap-1">
          {availableOptions.length === 0 ? (
            <div className="px-2 py-1.5 text-xs text-muted-foreground">
              <span key={tUi("noOptionsAvailable")}>{tUi("noOptionsAvailable")}</span></div>
          ) : (
            availableOptions.map((opt) => {
              const checked = selectedValues.includes(opt.value)
              const id = `multi-${labelPrefix}-${opt.value}`
              return (
                <Label
                  key={opt.value}
                  htmlFor={id}
                  className="flex items-start gap-2 rounded-sm px-2 py-1.5 text-sm font-normal cursor-pointer hover:bg-muted"
                >
                  <Checkbox
                    id={id}
                    checked={checked}
                    onCheckedChange={() => toggle(opt.value)}
                    className="mt-0.5"
                  />
                  <div className="flex-1 min-w-0">
                    <div className="truncate">{opt.label}</div>
                    {opt.subtitle && (
                      <div className="text-xs text-muted-foreground truncate">
                        {opt.subtitle}
                      </div>
                    )}
                  </div>
                  {checked && (
                    <Check className="h-3.5 w-3.5 mt-1 text-muted-foreground shrink-0" aria-hidden />
                  )}
                </Label>
              )
            })
          )}
          {selectedValues.length > 0 && (
            <>
              <div className="my-1 border-t" />
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="h-8 justify-start text-xs"
                onClick={() => onChange([])}
              >
                <span key={tUi("clearFilter")}>{tUi("clearFilter")}</span></Button>
            </>
          )}
        </div>
      </PopoverContent>
    </Popover>
  )
}
